import '../field/ui-field';
import { UITab } from './ui-tab';
import '../views/ui-views';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import {
    focusInteractiveElement,
    hide,
    position,
    show,
    updateClassList,
    updateElement,
} from '../../global/ui-helpers';
import {
    buildTabsBadge,
    buildIcon,
    renderButton,
    handleScrollButtonVisibility,
    handleScrollClickEvent,
} from './ui-tabs.helpers';
import { isKeyPressed, keyCodes, TabIndex } from '../../global/keyboard';
import { Digest } from '../../global/digest';
import { Labels } from '../../global/labels';
import { browser } from '../../global/browser';
import { throttle } from '../../global/helpers';
import type {
    UITabsLayouts,
    UITabsLayout,
    UITabsLayoutsMobile,
} from './ui-tabs.type';
import type {
    AttributeValue,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { UIViews } from '../views/ui-views';
import type { NullOr, ScrollContext } from '../../global/utils.type';
import type { UIField } from '../field/ui-field';
import { ComponentState } from '../ui-element.const';
import styles from './ui-tabs.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITabs
 * @element ui-tabs
 * @classdesc Represents a class for <code>ui-tabs</code> element. Accepts only UITab children.
 * @property {("outer" | "inner" | "buttons")} [layout="outer"] {@attr layout} Layout of the tabs.
 *  {@desc outer: default tabs}
 *  {@desc inner: tabs used as sections}
 *  {@desc buttons: deprecated, will be removed}
 * @property {string} [label] {@attr label} Label for dropdown.
 * @property {string} [heading] {@attr heading} Heading (visually hidden) for the tabs.
 * @property {("vertical" | "dropdown")} [layoutMobile] {@attr layout-mobile}
 *   Layout for smaller screen.
 *  {@desc vertical: vertically aligned tabs}
 *  {@desc dropdown: uses native select as dropdown}
 * @property {boolean} [focusonchange] {@attr focusonchange} Force focus to
 * the first interactive element
 * @property {HTMLUListElement} captions 
 * @property {UIViews} views 
 * @property {NodeList<HTMLButtonElement>} controls Collection of tab buttons.
 * @property {HTMLSelectElement} select 
 * @property {UIDropdown} dropdown 
 * @property {UIField} dropdownField 
 * @slot {@type "ui-tab"}
 * @example
 <ui-tabs heading="Heading for the tabs">
   <ui-tab label="Tab 1">
     Tab 1 content
   </ui-tab>
   <ui-tab label="Tab 2" active="true">
    Tab 2 content
   </ui-tab>
   <ui-tab label="Tab 3" label-badge="Badge">
    Tab 3 content
   </ui-tab>
   <ui-tab label="Tab 4" disabled>
    Tab 4 content
   </ui-tab>
 </ui-tabs>
 */
@Component({ tag: 'ui-tabs', styles })
export class UITabs extends UIElement {
    static MAX_TABS_FOR_FIXED_LAYOUT = 3;

    /**
     * @type {{OUTER: string, INNER: string}}
     */
    static readonly LAYOUT: UITabsLayouts = {
        OUTER: 'outer',
        INNER: 'inner',
        BUTTONS: 'buttons',
    };

    /**
     * @type {{DROPDOWN: string}}
     */
    static readonly LAYOUT_MOBILE: UITabsLayoutsMobile = {
        DROPDOWN: 'dropdown',
    };

    /**
     * @type {Array<string>}
     */
    static readonly WATCHED_ATTRIBUTES: ObservedAttributes = [
        'label',
        'label-badge',
        'link-to',
        'target',
        'active',
        'disabled',
    ];

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-tabs', {
            selectTab: 'Select tab',
            left: 'Scroll to the left',
            right: 'Scroll to the right',
        });
    }

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['layout-mobile', 'focusonchange'];
    }

    @Attribute(String) layout: UITabsLayout = UITabs.LAYOUT.OUTER;
    @Attribute(String) layoutMobile?: string;
    @Attribute(String) label?: string;
    @Attribute(String) heading?: string;
    @Attribute(Boolean) focusonchange?: boolean;

    @Child('.ui-tabs__dropdown select') select?: HTMLSelectElement;
    @Child('.ui-tabs__dropdown') dropdownField?: UIField;
    @Child(':scope > .ui-tabs__views') views!: UIViews;
    @Child(':scope > .ui-tabs__captions-container')
    captionsContainer!: HTMLDivElement;
    @Child(':scope > .ui-tabs__captions-container > .ui-tabs__captions')
    captions!: HTMLUListElement;
    @Children(
        ':scope > .ui-tabs__captions-container > .ui-tabs__captions .ui-tabs__control'
    )
    controls!: NodeListOf<HTMLButtonElement>;
    @Child(
        ':scope > .ui-tabs__captions-container .ui-tabs__scroll-container[placement="left"] > button'
    )
    scrollButtonLeft!: HTMLButtonElement;
    @Child(
        ':scope > .ui-tabs__captions-container .ui-tabs__scroll-container[placement="right"] > button'
    )
    scrollButtonRight!: HTMLButtonElement;

    private viewsObserver?: MutationObserver;
    private mobileTransformed: boolean = false;
    private viewFocusOutHandler!: EventListener;
    private keyDownHandler!: EventListener;
    private viewsKeyDownHandler!: EventListener;
    private scrollClickHandler!: EventListener;
    private scrollHandler!: EventListener;
    private resizeHandler!: EventListener;

    /**
     * Gets control element.
     * @param {string} viewId
     * @returns {string | null}
     */
    getControlId(viewId: string): NullOr<string> {
        return viewId ? viewId + '-control' : null;
    }

    /**
     * Gets scroll context.
     * @returns {ScrollContext}
     * @private
     */
    get scrollContext(): ScrollContext {
        return {
            captions: this.captions,
            scrollButtonLeft: this.scrollButtonLeft,
            scrollButtonRight: this.scrollButtonRight,
        };
    }

    /**
     * Shows tab contents.
     */
    showContent(): void {
        this.updateClassList({
            '-nocontent': false,
        });
    }

    /**
     * Hides tab content, only captions are presented.
     */
    hideContent(): void {
        this.updateClassList({
            '-nocontent': true,
        });
    }

    /**
     * Changes active tab.
     * @param {boolean} [initial] Flag for initial state, when the initial state is set,
     * do not focus on tabs children elements.
     * @returns {UITabs}
     */
    changeActiveTab(initial: boolean = false): UITabs {
        this.observeStop();
        this.captions
            .querySelectorAll('.ui-tabs__caption')
            .forEach((node, index) => {
                const isActive = index === this.views.selectedIndex;
                updateClassList(node, { '-active': isActive });
                const control = node.querySelector('.ui-tabs__control');
                if (control) {
                    control.setAttribute('aria-selected', String(isActive));
                    control.setAttribute(
                        'tabindex',
                        String(isActive ? TabIndex.Active : TabIndex.Inactive)
                    );
                }
            });

        if (this.select) {
            this.select.selectedIndex = this.views.selectedIndex;
        }

        if (
            this.focusonchange &&
            this.layout !== UITabs.LAYOUT.BUTTONS &&
            !initial
        ) {
            const view = this.views.getActiveView();
            if (view) {
                focusInteractiveElement(view, false, true);
            }
        }
        this.observe();
        return this;
    }

    /**
     * Checks if has some 'active' attribute set.
     * @param {UITabs} [tabs]
     * @returns {boolean}
     * @private
     */
    private hasAnyActiveCaption(tabs?: UITabs): boolean {
        return Array.from((tabs || this).controls).some(
            (control) => control.getAttribute('aria-selected') === 'true'
        );
    }

    /**
     * Hides/shows mobile select in layout="dropdown"
     * @private
     * @returns {UITabs}
     */
    private handleChangeAppearance(): this {
        if (this.mobileTransformed && browser.mobileMediaMatches()) {
            if (this.dropdownField) {
                show(this.dropdownField);
            }
            hide(this.captionsContainer);
        } else {
            show(this.captionsContainer);
            if (this.dropdownField) {
                hide(this.dropdownField);
            }
        }

        return this;
    }

    /**
     * Fires callback when the tab caption has been clicked.
     * @private
     * @param {PointerEvent} e
     */
    private handleChangeTab(e: PointerEvent): void {
        const caption = (e.target as HTMLElement).closest<HTMLElement>(
            '.ui-tabs__caption'
        );
        const elem = e.target as HTMLElement & { target?: string };
        if (elem.localName === 'a' && elem.target) {
            return;
        }
        this.views.selectByIndex(position(caption!));
    }

    /**
     * Fires callback when the tab caption has been clicked.
     * @private
     * @param {MouseEvent} e
     */
    private handleChangeView(e: MouseEvent): void {
        if ((e.target as HTMLElement).localName !== 'ui-views') {
            return;
        }
        this.changeActiveTab();
    }

    /**
     * @param {UITab} tab
     * @param {number} i
     * @returns {ElementConfig}
     */
    renderControl(tab: UITab, i: number): ElementConfig {
        const linkTo = tab.linkTo;
        const hasLink = !!linkTo;
        const target = tab.target;
        const isDisabled = this.isTabDisabled(tab);

        if (!tab.hasAttribute('id')) {
            tab.id = Digest.randomId();
        }
        const config: ElementConfig = {
            tagName: hasLink ? 'a' : 'button',
            events: {},
            attributes: {
                'id': this.getControlId(tab.id),
                'role': 'tab',
                'aria-selected': tab.active ? 'true' : 'false',
                'aria-controls': tab.id,
                'aria-disabled': isDisabled ? 'true' : null,
                'target': target || null,
                'rel': target ? 'noopener' : null,
                'data-tab-index': String(i),
                'type': !hasLink ? 'button' : null,
                'href': hasLink ? linkTo : null,
                'tabindex':
                    isDisabled || !tab.active
                        ? TabIndex.Inactive
                        : TabIndex.Active,
            },
            classList: {
                'ui-tabs__control': true,
            },
            children: [
                {
                    tagName: 'span',
                    classList: { 'ui-tabs__text': true },
                    children: [document.createTextNode(tab.label!)],
                },
            ],
        };

        if (tab.labelBadge) {
            (config?.children as Array<HTMLElement>)?.push(
                buildTabsBadge(tab.labelBadge)!
            );
        }

        if (hasLink && target === '_blank') {
            (config.children as Array<unknown>).push(
                buildIcon('redesign-new-window')
            );
        }

        return config;
    }

    /**
     * Removes dropdownField element
     * @private
     * @returns {UITabs}
     */
    private removeLayoutMobile(): UITabs {
        if (this.dropdownField) {
            this.removeChild(this.dropdownField);
        }
        return this;
    }

    /**
     * Renders select option.
     * @param {UITab} tab
     * @returns {ElementConfig}
     */
    renderSelectOption(tab: UITab): ElementConfig {
        return {
            tagName: 'option',
            children: [document.createTextNode(tab.label || '')],
            attributes: {
                'data-badge': tab.labelBadge || null,
                'data-link-to': tab.linkTo || null,
                'data-target': tab.target || null,
                'data-layout': this.layout,
                'selected': tab.active,
            },
        };
    }

    /**
     * Renders mobile dropdown layout
     * @param {Array<UITab>} tabs
     * @returns {UITabs}
     */
    renderLayoutMobile(tabs: Array<UITab>): UITabs {
        if (this.layoutMobile === UITabs.LAYOUT_MOBILE.DROPDOWN) {
            const selectElement = this.createElement({
                tagName: 'select',
                attributes: {
                    'aria-label': UITabs.labels.selectTab,
                },
                children: tabs.map(this.renderSelectOption.bind(this)),
            });
            const dropdownField = this.createElement({
                tagName: 'ui-field',
                attributes: {
                    class: 'ui-tabs__dropdown',
                    label: this.label,
                    layout: 'fluid',
                },
                children: [selectElement],
            });
            this.insertBefore(dropdownField, this.firstChild);
        }
        return this;
    }

    /**
     * Renders caption
     * @param {UITab} tab
     * @param {number} i
     * @private
     * @returns {ElementConfig}
     */
    private renderCaption(tab: UITab, i: number): ElementConfig {
        const control = this.renderControl(tab, i);
        const isDisabled = this.isTabDisabled(tab);
        const isHidden = this.isTabHidden(tab);

        return {
            tagName: 'li',
            attributes: {
                role: 'presentation',
            },
            classList: {
                'ui-tabs__caption': true,
                '-active': !!tab.active,
                '-disabled': isDisabled,
                '-hidden': isHidden,
            },
            children: [control],
        };
    }

    /**
     * Handles component mutations
     * @param {MutationRecord} mutation
     * @private
     */
    private handleAttributesMutation(mutation: MutationRecord): void {
        const attribute = mutation.attributeName;
        const target = mutation.target as UITab;
        if (
            ['label', 'label-badge', 'link-to', 'target'].includes(attribute!)
        ) {
            const tab = target;
            const index = [...this.views.children].findIndex(
                (view) => view === tab
            );
            const caption = this.captions.children[index];
            const config = this.renderControl(tab, index);
            const control = this.createElement(config);
            caption.replaceChild(control, caption.firstChild!);

            if (!(control as HTMLLinkElement).href) {
                this.subscribeClickEvent(control);
            }

            if (this.select) {
                const oldOption = this.select.children[index];
                const newOption = this.createElement(
                    this.renderSelectOption(tab)
                );
                this.select.replaceChild(newOption, oldOption);
            }
        } else if (mutation.attributeName === 'active') {
            this.changeActiveTab();
        }
    }

    /**
     * Fires callback when component mutates.
     * @param {Array<MutationRecord>} mutations
     * @private
     */
    private handleMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            const target = mutation.target;
            if (!(target instanceof UITab)) {
                continue;
            }
            const parentTabs = target.closest('ui-tabs');
            if (parentTabs !== this) {
                continue;
            }
            if (mutation.type === 'attributes') {
                this.handleAttributesMutation(mutation);
            }
        }
    }

    /**
     * Starts observing component
     * @private
     */
    private observe(): void {
        if (!this.viewsObserver) {
            return;
        }
        this.viewsObserver.observe(this.views, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: UITabs.WATCHED_ATTRIBUTES,
        });
    }

    /**
     * Stops observing component
     * @private
     */
    private observeStop(): void {
        this.viewsObserver?.disconnect();
    }

    /**
     * Adds click event listener for tab control button.
     * @param {HTMLElement} control
     */
    subscribeClickEvent(control: HTMLElement): void {
        updateElement(control, {
            events: {
                click: this.handleChangeTab.bind(this) as () => void,
            },
        });
    }

    /**
     * Adds keydown event listener for tab control button (role="tab" as well).
     * @param {HTMLElement} control
     */
    subscribeKeydownEvent(control: HTMLButtonElement | HTMLLinkElement): void {
        updateElement(control, {
            events: {
                keydown: ((event: KeyboardEvent) => {
                    if (
                        isKeyPressed(event, keyCodes.SPACE) &&
                        (event.target as HTMLElement).getAttribute('role') ===
                            'tab'
                    ) {
                        event.preventDefault();
                        (event.target as HTMLElement).click();
                    }
                }) as () => void,
            },
        });
    }

    /**
     * Listens to DOM events
     * @private
     * @returns {UITabs}
     */
    private listenEvents(): this {
        updateElement(this.views, {
            events: {
                change: this.handleChangeView.bind(this) as () => void,
            },
        });

        this.controls.forEach((btn) => {
            if (!(btn as unknown as HTMLLinkElement).href) {
                this.subscribeClickEvent(btn);
            }

            this.subscribeKeydownEvent(btn);
        });
        return this;
    }

    /**
     * Listens mobile-layout events
     * @private
     * @returns {UITabs}
     */
    private listenLayoutEvents(): this {
        if (this.layoutMobile === UITabs.LAYOUT_MOBILE.DROPDOWN) {
            updateElement(this.select!, {
                events: {
                    change: (e: Event) => {
                        const selectEl = e.target as HTMLSelectElement;
                        const option = selectEl.selectedOptions[0];
                        if (option?.dataset.linkTo) {
                            window.open(
                                option.dataset.linkTo,
                                option.dataset.target || '_self'
                            );
                            selectEl.selectedIndex = this.views.selectedIndex;
                        } else {
                            this.views.selectByIndex(selectEl.selectedIndex);
                        }
                    },
                },
            });
            this.mobileTransformed = true;
            browser.mobileMedia.addListener(
                this.handleChangeAppearance.bind(this)
            );
        } else {
            this.mobileTransformed = false;
        }

        return this;
    }

    /**
     * Activates default view for views
     * @param {Array<UITab>} tabs
     * @returns {UITabs}
     */
    private activateDefaultView(tabs: Array<UITab>): this {
        this.views.selectedIndex = tabs.findIndex(
            (tab) => tab.active && !this.isTabHidden(tab)
        );
        this.views.activateDefaultView();
        return this;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }
        const tabs = this.views.queryChildren('ui-tab') as Array<UITab>;
        switch (name) {
            case 'layout-mobile':
                this.removeLayoutMobile()
                    .renderLayoutMobile(tabs)
                    .listenLayoutEvents()
                    .handleChangeAppearance();
                break;
            case 'focusonchange':
                updateElement(this.views, {
                    attributes: {
                        focusonchange: this.focusonchange ? true : null,
                    },
                });
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @private
     * @param {FocusEvent} [event]
     */
    private handleViewsFocusOut(event: FocusEvent): void {
        if (
            !this.isTabControl(document.activeElement as HTMLElement) ||
            !this.hasAnyActiveCaption()
        ) {
            return;
        }
        let tabs = (event.target as HTMLElement).closest<UITabs>('ui-tabs');
        const controls = tabs === this ? this.controls : tabs?.controls;
        for (const control of Array.from(controls!)) {
            tabs = control.closest<UITabs>('ui-tabs')!;
            const hasFocusedControl = Array.from(tabs.controls).some(
                (ctrl) => ctrl === document.activeElement
            );
            const isFromTab =
                (event.target as HTMLElement).localName === 'ui-tab';
            if (
                (
                    [
                        UITabs.LAYOUT.INNER,
                        UITabs.LAYOUT.BUTTONS,
                    ] as Array<UITabsLayout>
                ).includes(tabs.layout) &&
                hasFocusedControl &&
                !isFromTab
            ) {
                break;
            }
            if (control.ariaSelected === 'true') {
                if (control.offsetParent !== null) {
                    event.preventDefault();
                    control.focus();
                }
                break;
            }
        }
    }

    /**
     * @param {Element} element
     * @returns {boolean}
     */
    isTabControl(element: NullOr<HTMLElement>): boolean {
        return !!element?.classList.contains('ui-tabs__control');
    }

    /**
     * @private
     * @param {KeyboardEvent} event
     */
    private handleKeyDown(event: KeyboardEvent): void {
        if (isKeyPressed(event, keyCodes.TAB) && !event.shiftKey) {
            const activeView = this.views.getActiveView();
            if (activeView) {
                event.preventDefault();
                activeView.focus();
            }
            return;
        }

        let i = Array.from(this.controls).indexOf(
            document.activeElement as HTMLButtonElement
        );
        switch (event.keyCode) {
            case keyCodes.DOWN:
            case keyCodes.RIGHT:
                i = this.adjustIndex(event, i, 1);
                break;
            case keyCodes.UP:
            case keyCodes.LEFT:
                i = this.adjustIndex(event, i, -1);
                break;
        }
        this.controls[i]?.focus();
    }

    /**
     * Adjusting selected element index higher or lower when using arrow keys.
     * @param {KeyboardEvent} event
     * @param {number} i
     * @param {number} increment
     */
    adjustIndex(event: KeyboardEvent, i: number, increment: number): number {
        event.preventDefault();
        i = (i + increment + this.controls.length) % this.controls.length;
        return i;
    }

    /**
     * Checks if a tab is disabled.
     * @param {UITab} tab
     * @returns {boolean}
     */
    isTabDisabled(tab: UITab): boolean {
        return !!tab.disabled || tab.hasAttribute('disabled');
    }

    /**
     * Checks if a tab is hidden.
     * @param {UITab} tab
     * @returns {boolean}
     */
    isTabHidden(tab: UITab): boolean {
        return !!tab.hidden || tab.classList.contains('-hidden');
    }

    /**
     * Handles keydown events in the tab__views area to catch Shift+Tab and focus on the active tab.
     * @private
     * @param {KeyboardEvent} event
     */
    private handleViewsKeyDown(event: KeyboardEvent): void {
        if (event.shiftKey && isKeyPressed(event, keyCodes.TAB)) {
            const activeView = this.views.getActiveView();
            if (!activeView) {
                return;
            }

            if (event.target === activeView) {
                const activeIndex = Array.from(this.controls).findIndex(
                    (control) =>
                        control.parentElement?.classList.contains('-active')
                );
                if (activeIndex >= 0) {
                    const control = this.controls[activeIndex];
                    if (control.offsetParent !== null) {
                        event.preventDefault();
                        control.focus();
                    }
                }
            }
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const elements: Array<HTMLElement | null> = [];
        const tabs = this.queryChildren('ui-tab') as Array<UITab>;
        this.redetachChildNodes();

        // Only relevant when this.heading is provided
        let headingId: NullOr<string> = null;
        if (this.heading) {
            headingId = Digest.randomId(8, 'heading-');
            const headingEl = this.createElement({
                tagName: 'h2',
                classList: {
                    'ui-tabs__heading': true,
                    '-visually-hidden': true,
                },
                attributes: {
                    id: headingId,
                },
                children: [this.heading],
            });
            elements.push(headingEl);
        }

        const captionsId = Digest.randomId(8, 'captions-');
        const captions = this.createElement({
            tagName: 'div',
            classList: {
                'ui-tabs__captions-container': true,
            },
            children: [
                renderButton('left', captionsId),
                {
                    tagName: 'ul',
                    classList: {
                        'ui-tabs__captions': true,
                    },
                    attributes: {
                        'role': 'tablist',
                        'id': captionsId,
                        'aria-labelledby': this.heading ? headingId : null,
                    },
                    children: tabs.map(this.renderCaption.bind(this)),
                },
                renderButton('right', captionsId),
            ],
        });
        elements.push(captions);

        const views = this.createElement({
            tagName: 'ui-views',
            classList: {
                'ui-tabs__views': true,
            },
            attributes: {
                filter: 'ui-tab',
                focusonchange: this.focusonchange ? true : null,
            },
            children: tabs.map((tab) => {
                tab.setAttribute('aria-labelledby', this.getControlId(tab.id)!);
                return tab;
            }),
        });
        elements.push(views);

        this.renderLayoutMobile(tabs).insertElements(elements.filter(Boolean));
        this.activateDefaultView(tabs)
            .changeActiveTab(true)
            .handleChangeAppearance();
        this.updateElement({
            attributes: {
                layout: this.layout,
            },
        });

        handleScrollButtonVisibility(this.scrollContext);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.viewFocusOutHandler = this.handleViewsFocusOut.bind(
            this
        ) as EventListener;
        this.keyDownHandler = this.handleKeyDown.bind(this) as EventListener;
        this.viewsKeyDownHandler = this.handleViewsKeyDown.bind(
            this
        ) as EventListener;
        this.scrollClickHandler = (event: Event) => {
            handleScrollClickEvent(event as MouseEvent, this.scrollContext);
        };
        this.scrollHandler = throttle(() => {
            handleScrollButtonVisibility(this.scrollContext);
        }, 100);
        this.resizeHandler = throttle(() => {
            handleScrollButtonVisibility(this.scrollContext);
        }, 100);
        this.listenEvents().listenLayoutEvents().handleChangeAppearance();
        this.viewsObserver = new MutationObserver(
            this.handleMutations.bind(this)
        );

        window.addEventListener('resize', this.resizeHandler);
        this.views.addEventListener('focusout', this.viewFocusOutHandler);
        this.views.addEventListener('keydown', this.viewsKeyDownHandler, true);
        this.captions.addEventListener('keydown', this.keyDownHandler);
        this.captions.addEventListener('scroll', this.scrollHandler);
        this.scrollButtonLeft.addEventListener(
            'click',
            this.scrollClickHandler
        );
        this.scrollButtonRight.addEventListener(
            'click',
            this.scrollClickHandler
        );
        this.observe();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.viewsObserver?.disconnect();
        window.removeEventListener('resize', this.resizeHandler);
        this.views.removeEventListener('focusout', this.viewFocusOutHandler);
        this.views.removeEventListener('keydown', this.viewsKeyDownHandler);
        this.captions.removeEventListener('keydown', this.keyDownHandler);
        this.captions.removeEventListener('scroll', this.scrollHandler);
        this.scrollButtonLeft.removeEventListener(
            'click',
            this.scrollClickHandler
        );
        this.scrollButtonRight.removeEventListener(
            'click',
            this.scrollClickHandler
        );
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        window.addEventListener('resize', this.resizeHandler);
        this.views.addEventListener('focusout', this.viewFocusOutHandler);
        this.views.addEventListener('keydown', this.viewsKeyDownHandler, true);
        this.captions.addEventListener('keydown', this.keyDownHandler);
        this.captions.addEventListener('scroll', this.scrollHandler);
        this.scrollButtonLeft.addEventListener(
            'click',
            this.scrollClickHandler
        );
        this.scrollButtonRight.addEventListener(
            'click',
            this.scrollClickHandler
        );
    }
}
